import React, { useState, useEffect } from "react"
import { Grid, GridColumn as Column, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { VerificationViewCell } from "./VerificationViewCell";
import { useNavigate, useParams } from "react-router";
import { useWorklistQuery } from "../../../../services/verifyService/verify-api";
import { ColumnMenu } from "../../../../utils/ColumnMenu";
import { Worklist } from "../../../../services/verifyService/model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography, Button } from "@mui/material";
import { WorklistRequest } from "../../../../services/verifyService/commands";
import { process, SortDescriptor, State } from "@progress/kendo-data-query";
import { useAllProfilesQuery, useUserAssignedCasesMutation } from "../../../../services/profileService/profile-api";
import { getProcessType } from "./utils/getProcessType";


function BenefitsWorklist(){
    const {type,center,filter} = useParams();
    // Check if filter is a user filter (user:upn)
    const isUserFilter = filter?.startsWith('user:');
    const userUpn = isUserFilter ? decodeURIComponent(filter!.substring(5)) : null;
    // When filtering by user, we need to fetch ALL cases (use 'NA' as filter for API)
    const apiFilter = isUserFilter ? 'NA' : filter!;
    
    const {data} = useWorklistQuery(new WorklistRequest(type!,center!,apiFilter));
    const { data: allProfiles } = useAllProfilesQuery();
    const navigate = useNavigate();
    const [getUserAssignedCases] = useUserAssignedCasesMutation();
    const [assignedAccounts, setAssignedAccounts] = useState<Set<string> | null>(null);
    const [userNameDisplay, setUserNameDisplay] = useState<string>('');

    // Fetch user's assigned cases from backend when filtering by user
    useEffect(() => {
        if (!isUserFilter || !userUpn || !data || data.length === 0) {
            setAssignedAccounts(null);
            return;
        }

        const processType = getProcessType(type!);

        // Map worklist data to request format
        const worklistItems = data.map(item => ({
            account: item.account,
            calcType: item.calcType,
            centerId: item.centerId,
            isOutsourced: item.isOutsourced
        }));

        getUserAssignedCases({
            upn: userUpn,
            worklistItems,
            processType,
            worklistType: type!
        }).then(result => {
            if ('data' in result && result.data) {
                setAssignedAccounts(new Set(result.data.assignedAccounts));
                setUserNameDisplay(result.data.userName);
            }
        }).catch(error => {
            console.error('Error getting user assigned cases:', error);
            setAssignedAccounts(new Set());
        });
    }, [isUserFilter, userUpn, data, type, getUserAssignedCases]);

    const mapCases=():Worklist[]=>{
        let cases:any=data===undefined ? [] : data?.map((i:any) => {
            const serviceDate = i.serviceDate ? new Date(i.serviceDate) : new Date();
            const startDate = i.startDate ? new Date(i.startDate) : new Date();
            const today = new Date();
            const daysUntil = Math.ceil((serviceDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            return {
                ...i, 
                serviceDate: serviceDate, 
                startDate: startDate,
                daysUntilDos: daysUntil
            };
        })
        //Filter Adec Outsourced cases
        if(type === 'AdecBenefits'){
            cases = cases.filter((item: any)=> item.isOutsourced === true);}
        if(type === 'Benefits'){
            cases = cases.filter((item: any)=> item.isOutsourced === false);}
        
        // Apply user filter with backend-calculated assigned accounts
        if (isUserFilter && assignedAccounts) {
            cases = cases.filter((item: any) => assignedAccounts.has(item.account));
        }
        
        return cases;
    }
    
    const initialSort:SortDescriptor[] = [{field:'daysUntilDos', dir:'asc'}];
    const defaultGridState:State = {sort:initialSort};
    const [dataState, setDataState] = useState(defaultGridState);
    const createDataState = (dataState: State) => {
        return {
          dataState: dataState === undefined ? {} :dataState,};
      }; 
    const dataStateChange = (event: GridDataStateChangeEvent) => {
        let updatedState = createDataState(event.dataState);
        setDataState(updatedState.dataState);
      };

    // Get display title - include user name if filtering by user
    const getTitle = (): string => {
        let title = type === 'CheckOonNotes' ? 'Check OON Notes Worklist' : type + ' Worklist';
        if (isUserFilter) {
            // Use the username from backend response, or fallback to finding from profiles
            if (userNameDisplay) {
                title += ` - ${userNameDisplay}`;
            } else if (allProfiles) {
                const userProfile = allProfiles.find(p => p.upn === userUpn);
                if (userProfile) {
                    const userName = userProfile.identity || userProfile.email || userUpn || '';
                    if (userName) {
                        title += ` - ${userName}`;
                    }
                }
            }
        }
        return title;
    };

    return <React.Fragment>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography className="psn-green" variant='h6'>{getTitle()}</Typography>
            <Button variant='outlined' onClick={()=>navigate(-1)} color='success'><FontAwesomeIcon icon={'arrow-left'}/><span className="ms-1">Go Back</span></Button>
        </Stack>

        <Grid
        key={`${type}-${center}-${filter}`}
        data={process(mapCases(), dataState)}
        {...dataState}
        style={{ height: '750px', width: '100%' }}
        sortable={{mode:'multiple'}}
        groupable={false}
        onDataStateChange={dataStateChange}
>
            <Column cell={VerificationViewCell} width={'75px'} groupable={false}/>
            {center === 'ALL' && <Column field="centerName" title="Center" width="150px" columnMenu={ColumnMenu} />}
            {center === 'ALL' && <Column field="npi" title="NPI" width="150px" columnMenu={ColumnMenu} />}
            <Column title='Patient' field='patient' columnMenu={ColumnMenu} width={'200px'}/>
            <Column title='Account' field='account' columnMenu={ColumnMenu} width={'150px'}/>
            <Column title='Service Date' field='serviceDate' columnMenu={ColumnMenu} format='{0:MM/dd/yyyy}' filter="date" width={'140px'}/>
            <Column title='Physician' field='physician' columnMenu={ColumnMenu} width={'150px'}/>
            <Column title='Type' field="admitType" columnMenu={ColumnMenu} width={'100px'}/>
            <Column title='Admit Source' field='admitSource' columnMenu={ColumnMenu} width={'180px'}/>
            <Column title='Payer' field="payerName" columnMenu={ColumnMenu} width={'250x'}/>
            {type === 'Implant Variance' &&
            <Column title='Implant Variance' field='implantVariance' columnMenu={ColumnMenu} format='{0:c2}' width={'150px'}/>}
            {(type === 'Authorization' || type==='Authorization Denied') &&
            <Column title='Auth Status' field='authStatus' columnMenu={ColumnMenu} width={'200px'}/>}
            {(type === 'Authorization' || type==='Authorization Denied') && 
            <Column title='EAR Status' field='earStatus' columnMenu={ColumnMenu} width={'200px'}/>}
            {type === 'Financial Orientation' &&
            <Column title='Call Count' field='callCount' columnMenu={ColumnMenu} width={'150px'}/>           
            }
            <Column title='VOB Created' field='startDate' columnMenu={ColumnMenu} format='{0:MM/dd/yyyy}' filter="date" width={'140px'}/>
            <Column title='Days Until DOS' field='daysUntilDos' columnMenu={ColumnMenu} filter="numeric" width={'170px'}/>
        </Grid>
    </React.Fragment>
}

export {BenefitsWorklist}